import { convertBreadcrumbsData } from '@/components/NewUI/Layout/Header/Components/breadcrumbData';
import { BREADCRUMB_UPDATE_EVENT } from '@/components/NewUI/ModernLayout/layoutContract';

import { trackClientNavigation } from './analytics';
import { unnamedEntityOf } from './crumbs';
import { locationDocument, shellContextOf } from './documents';
import { admittedRouteFor, staticDataOf } from './navigation';
import { navigationAccessibility } from './NavigationAccessibility';
import { recordNamesAt, storeRecordNames, toRecordNames } from './recordNames';
import { applyDocumentTitle, resolvePageName } from './title';

import type { AnyRouter } from '@tanstack/react-router';

import type { BreadcrumbItem } from '@/components/NewUI/Layout/Header/Components/BreadcrumbTrail';

import type { RecordNames } from './types';

interface ResolvedBreadcrumbs {
  items: BreadcrumbItem[] | null;
  /** True when the items are the server document's own crumbs. */
  fromServer: boolean;
}

/** Declared crumbs with the names the shell knows for the entities their routes left unnamed. */
export const nameEntityCrumbs = (router: AnyRouter, items: BreadcrumbItem[]): BreadcrumbItem[] =>
  items.map((item) => {
    const entityParam = unnamedEntityOf(item);
    const name = entityParam && shellContextOf(router)?.entityNames(entityParam);
    return name ? { ...item, name } : item;
  });

/**
 * Header crumbs for the resolved location, most specific first: an admitted route's typed crumbs,
 * with the record names its page reported (null when its page dispatches its own), then the
 * server document's crumbs (served or probed), then one crumb naming the page. A document
 * without crumbs counts as none. Null while the shell knows nothing about the location yet.
 */
const resolve = (router: AnyRouter): ResolvedBreadcrumbs => {
  const { location, matches } = router.state;
  const route = admittedRouteFor(router, location.pathname);
  const { breadcrumbs } = staticDataOf(route);
  if (breadcrumbs) {
    const params = (matches.at(-1)?.params ?? {}) as Record<string, string>;
    const items = breadcrumbs(params, recordNamesAt(router, location.pathname));
    return { items: items && nameEntityCrumbs(router, items), fromServer: false };
  }
  const serverDocument = locationDocument(router);
  const serverItems = convertBreadcrumbsData(
    serverDocument?.breadcrumbsHtml ?? '',
    location.pathname
  );
  if (serverItems.length > 0) return { items: serverItems, fromServer: true };
  if (!route && !serverDocument) return { items: null, fromServer: false };
  const name = resolvePageName(router);
  return { items: name ? [{ name, url: '' }] : [], fromServer: false };
};

export const resolveBreadcrumbs = (router: AnyRouter): BreadcrumbItem[] | null =>
  resolve(router).items;

const dispatchItems = (items: BreadcrumbItem[]): void => {
  window.dispatchEvent(new CustomEvent(BREADCRUMB_UPDATE_EVENT, { detail: { items } }));
};

// Never `null`: that would restore the first document's crumbs on another page.
export const dispatchBreadcrumbs = (router: AnyRouter): void => {
  const { items } = resolve(router);
  if (items) dispatchItems(items);
};

// The Header renders the served document's crumbs on first load; other crumbs must be sent.
export const dispatchServedBreadcrumbs = (router: AnyRouter): void => {
  const { fromServer, items } = resolve(router);
  if (items && !fromServer) dispatchItems(items);
};

/**
 * Keeps the names a page reports for `pathname` and, while the router is still there, applies
 * the title and sends the crumbs they name. Server crumbs never name a record: they stay.
 */
export const setRecordName = (
  router: AnyRouter,
  pathname: string,
  names: string | RecordNames
): void => {
  storeRecordNames(router, pathname, toRecordNames(names));
  if (router.state.location.pathname !== pathname) return;
  applyDocumentTitle(router);
  dispatchServedBreadcrumbs(router);
};

/**
 * Title, crumbs and pageview analytics for a probed location, once the server's document for it
 * arrives. Admitted routes get the same from the router's `onResolved` as soon as they resolve.
 */
export const announceProbedLocation = (router: AnyRouter): void => {
  const { location } = router.state;
  applyDocumentTitle(router);
  navigationAccessibility(router).probeReady();
  dispatchBreadcrumbs(router);
  trackClientNavigation(`${location.pathname}${location.searchStr}`);
};
